/**
 * Board games for the office. Every rule lives here rather than in the server
 * or the client: the API validates moves with the same functions the board UI
 * uses to highlight them, so the two can never disagree about what is legal.
 *
 * A game state is a flat, row-major array of cell codes plus whatever
 * bookkeeping that game needs (castling rights, a jump chain, a move clock).
 * It is plain JSON, so it stores in Mongo and crosses the socket unchanged.
 */
export const GAME_KINDS = [
    'tic_tac_toe',
    'connect_four',
    'gomoku',
    'hex',
    'backgammon',
    'reversi',
    'checkers',
    'chess',
];
export const GAME_INFO = {
    tic_tac_toe: {
        label: 'Tic-tac-toe',
        hint: 'Three in a row. Long enough for a coffee to brew.',
        icon: '⊞',
        seatLabels: ['X', 'O'],
        seatColors: ['#38bdf8', '#f472b6'],
        minutes: '1 min',
    },
    connect_four: {
        label: 'Connect Four',
        hint: 'Drop discs down a column; four in a line wins.',
        icon: '◍',
        seatLabels: ['Red', 'Yellow'],
        seatColors: ['#ef4444', '#eab308'],
        minutes: '5 min',
    },
    gomoku: {
        label: 'Gomoku',
        hint: 'Place stones on the grid. The first five in a line wins.',
        icon: '⦿',
        seatLabels: ['Black', 'White'],
        seatColors: ['#172033', '#f8fafc'],
        minutes: '10 min',
    },
    hex: {
        label: 'Hex',
        hint: 'Build an unbroken path between your two coloured edges.',
        icon: '⬡',
        seatLabels: ['Red', 'Blue'],
        seatColors: ['#ef4444', '#3b82f6'],
        minutes: '15 min',
    },
    backgammon: {
        label: 'Backgammon',
        hint: 'Race every checker home. Enter from the bar before moving anything else.',
        icon: 'BG',
        seatLabels: ['Ivory', 'Burgundy'],
        seatColors: ['#f6e7c8', '#881f32'],
        minutes: '20 min',
    },
    reversi: {
        label: 'Reversi',
        hint: 'Flank a line to flip it. Most discs at the end wins.',
        icon: '◑',
        seatLabels: ['Black', 'White'],
        seatColors: ['#1e293b', '#f8fafc'],
        minutes: '15 min',
    },
    checkers: {
        label: 'Checkers',
        hint: 'Jumps are compulsory. Reach the far row to be crowned.',
        icon: '⛂',
        seatLabels: ['Red', 'Black'],
        seatColors: ['#ef4444', '#334155'],
        minutes: '20 min',
    },
    chess: {
        label: 'Chess',
        hint: 'Full rules — castling, en passant, promotion, stalemate.',
        icon: '♞',
        seatLabels: ['White', 'Black'],
        seatColors: ['#f8fafc', '#1e293b'],
        minutes: '30 min+',
    },
};
/** Glyphs for every cell code, so web and mobile draw the same pieces. */
export const GAME_GLYPHS = {
    tic_tac_toe: { X: '✕', O: '◯' },
    connect_four: { R: '●', Y: '●' },
    gomoku: { B: '●', W: '●' },
    hex: { R: '●', B: '●' },
    backgammon: { A: '●', B: '●' },
    reversi: { B: '●', W: '●' },
    checkers: { r: '⛂', R: '⛃', b: '⛂', B: '⛃' },
    chess: {
        K: '♔',
        Q: '♕',
        R: '♖',
        B: '♗',
        N: '♘',
        P: '♙',
        k: '♚',
        q: '♛',
        r: '♜',
        b: '♝',
        n: '♞',
        p: '♟',
    },
};
/* ------------------------------------------------------------------ */
/* Board helpers                                                       */
/* ------------------------------------------------------------------ */
const FILES = 'abcdefghijklmnopqrstuvwxyz';
export function cellIndex(row, col, cols) {
    return row * cols + col;
}
export function cellRow(index, cols) {
    return Math.floor(index / cols);
}
export function cellCol(index, cols) {
    return index % cols;
}
/** `a1`-style name, counting ranks from the bottom of the board. */
export function squareName(index, cols, rows) {
    return `${FILES[cellCol(index, cols)]}${rows - cellRow(index, cols)}`;
}
function inside(row, col, cols, rows) {
    return row >= 0 && row < rows && col >= 0 && col < cols;
}
function emptyBoard(cols, rows) {
    return new Array(cols * rows).fill('');
}
function clone(state) {
    return {
        ...state,
        board: state.board.slice(),
        // A store that strips empty objects can hand back a state without `meta`;
        // the engine treats a missing one as empty rather than throwing.
        meta: { ...(state.meta ?? {}) },
    };
}
const other = (seat) => (seat === 0 ? 1 : 0);
/** Games that start with no bookkeeping can come back from a store without it. */
export const metaOf = (state) => state.meta ?? {};
/* ------------------------------------------------------------------ */
/* Tic-tac-toe                                                         */
/* ------------------------------------------------------------------ */
const TTT_LINES = [
    [0, 1, 2],
    [3, 4, 5],
    [6, 7, 8],
    [0, 3, 6],
    [1, 4, 7],
    [2, 5, 8],
    [0, 4, 8],
    [2, 4, 6],
];
const ticTacToe = {
    cols: 3,
    rows: 3,
    create: () => ({
        kind: 'tic_tac_toe',
        cols: 3,
        rows: 3,
        board: emptyBoard(3, 3),
        turn: 0,
        outcome: null,
        moveCount: 0,
        meta: {},
    }),
    legalMoves(state) {
        const moves = [];
        state.board.forEach((cell, i) => {
            if (!cell)
                moves.push({ to: i });
        });
        return moves;
    },
    apply(state, move) {
        const next = clone(state);
        const mark = state.turn === 0 ? 'X' : 'O';
        next.board[move.to] = mark;
        next.lastMove = move;
        next.moveCount += 1;
        const line = TTT_LINES.find((cells) => cells.every((c) => next.board[c] === mark));
        if (line) {
            next.outcome = { winner: state.turn, reason: 'Three in a row' };
            next.meta.winningLine = line.join(',');
        }
        else if (next.board.every(Boolean)) {
            next.outcome = { winner: null, reason: 'The board filled up' };
        }
        else {
            next.turn = other(state.turn);
        }
        return next;
    },
    notation: (state, move) => `${state.turn === 0 ? 'X' : 'O'}${squareName(move.to, 3, 3)}`,
};
/* ------------------------------------------------------------------ */
/* Connect Four                                                        */
/* ------------------------------------------------------------------ */
const C4_COLS = 7;
const C4_ROWS = 6;
const C4_DIRS = [
    [0, 1],
    [1, 0],
    [1, 1],
    [1, -1],
];
const connectFour = {
    cols: C4_COLS,
    rows: C4_ROWS,
    create: () => ({
        kind: 'connect_four',
        cols: C4_COLS,
        rows: C4_ROWS,
        board: emptyBoard(C4_COLS, C4_ROWS),
        turn: 0,
        outcome: null,
        moveCount: 0,
        meta: {},
    }),
    legalMoves(state) {
        const moves = [];
        for (let col = 0; col < C4_COLS; col += 1) {
            // Discs fall, so the only playable cell in a column is its lowest gap.
            for (let row = C4_ROWS - 1; row >= 0; row -= 1) {
                const i = cellIndex(row, col, C4_COLS);
                if (!state.board[i]) {
                    moves.push({ to: i });
                    break;
                }
            }
        }
        return moves;
    },
    apply(state, move) {
        const next = clone(state);
        const disc = state.turn === 0 ? 'R' : 'Y';
        next.board[move.to] = disc;
        next.lastMove = move;
        next.moveCount += 1;
        const line = connectFourLine(next.board, move.to, disc);
        if (line) {
            next.outcome = { winner: state.turn, reason: 'Four in a line' };
            next.meta.winningLine = line.join(',');
        }
        else if (next.board.every(Boolean)) {
            next.outcome = { winner: null, reason: 'The board filled up' };
        }
        else {
            next.turn = other(state.turn);
        }
        return next;
    },
    notation(state, move) {
        const label = state.turn === 0 ? 'R' : 'Y';
        return `${label}${squareName(move.to, C4_COLS, C4_ROWS)}`;
    },
};
function connectFourLine(board, from, disc) {
    const row = cellRow(from, C4_COLS);
    const col = cellCol(from, C4_COLS);
    for (const [dr, dc] of C4_DIRS) {
        const line = [from];
        for (const sign of [1, -1]) {
            let r = row + dr * sign;
            let c = col + dc * sign;
            while (inside(r, c, C4_COLS, C4_ROWS) && board[cellIndex(r, c, C4_COLS)] === disc) {
                line.push(cellIndex(r, c, C4_COLS));
                r += dr * sign;
                c += dc * sign;
            }
        }
        if (line.length >= 4)
            return line.sort((a, b) => a - b);
    }
    return null;
}
/* ------------------------------------------------------------------ */
/* Gomoku (five in a row)                                              */
/* ------------------------------------------------------------------ */
const GM_SIZE = 15;
const gomoku = {
    cols: GM_SIZE,
    rows: GM_SIZE,
    create: () => ({
        kind: 'gomoku',
        cols: GM_SIZE,
        rows: GM_SIZE,
        board: emptyBoard(GM_SIZE, GM_SIZE),
        turn: 0,
        outcome: null,
        moveCount: 0,
        meta: {},
    }),
    legalMoves(state) {
        const moves = [];
        state.board.forEach((cell, to) => {
            if (!cell)
                moves.push({ to });
        });
        return moves;
    },
    apply(state, move) {
        const next = clone(state);
        const stone = state.turn === 0 ? 'B' : 'W';
        next.board[move.to] = stone;
        next.lastMove = move;
        next.moveCount += 1;
        const line = lineThrough(next.board, move.to, stone, GM_SIZE, GM_SIZE, 5);
        if (line) {
            next.outcome = { winner: state.turn, reason: 'Five in a row' };
            next.meta.winningLine = line.join(',');
        }
        else if (next.board.every(Boolean)) {
            next.outcome = { winner: null, reason: 'The board filled up' };
        }
        else {
            next.turn = other(state.turn);
        }
        return next;
    },
    notation: (state, move) => `${state.turn === 0 ? 'B' : 'W'}${squareName(move.to, GM_SIZE, GM_SIZE)}`,
};
function lineThrough(board, from, piece, cols, rows, length) {
    const originRow = cellRow(from, cols);
    const originCol = cellCol(from, cols);
    for (const [dr, dc] of C4_DIRS) {
        const line = [from];
        for (const sign of [1, -1]) {
            let row = originRow + dr * sign;
            let col = originCol + dc * sign;
            while (inside(row, col, cols, rows) && board[cellIndex(row, col, cols)] === piece) {
                line.push(cellIndex(row, col, cols));
                row += dr * sign;
                col += dc * sign;
            }
        }
        if (line.length >= length)
            return line.sort((a, b) => a - b);
    }
    return null;
}
/* ------------------------------------------------------------------ */
/* Hex                                                                 */
/* ------------------------------------------------------------------ */
const HX_SIZE = 11;
const HX_NEIGHBOURS = [
    [-1, 0],
    [-1, 1],
    [0, -1],
    [0, 1],
    [1, -1],
    [1, 0],
];
const hex = {
    cols: HX_SIZE,
    rows: HX_SIZE,
    create: () => ({
        kind: 'hex',
        cols: HX_SIZE,
        rows: HX_SIZE,
        board: emptyBoard(HX_SIZE, HX_SIZE),
        turn: 0,
        outcome: null,
        moveCount: 0,
        meta: {},
    }),
    legalMoves(state) {
        const moves = [];
        state.board.forEach((cell, to) => {
            if (!cell)
                moves.push({ to });
        });
        return moves;
    },
    apply(state, move) {
        const next = clone(state);
        const stone = state.turn === 0 ? 'R' : 'B';
        next.board[move.to] = stone;
        next.lastMove = move;
        next.moveCount += 1;
        const path = hexWinningPath(next.board, state.turn);
        if (path) {
            next.outcome = { winner: state.turn, reason: 'Connected both edges' };
            next.meta.winningLine = path.join(',');
        }
        else {
            // A completed Hex board always has one winner, so no draw branch is needed.
            next.turn = other(state.turn);
        }
        return next;
    },
    notation: (state, move) => `${state.turn === 0 ? 'R' : 'B'}${squareName(move.to, HX_SIZE, HX_SIZE)}`,
};
function hexWinningPath(board, seat) {
    const stone = seat === 0 ? 'R' : 'B';
    const starts = [];
    for (let offset = 0; offset < HX_SIZE; offset += 1) {
        const index = seat === 0 ? cellIndex(0, offset, HX_SIZE) : cellIndex(offset, 0, HX_SIZE);
        if (board[index] === stone)
            starts.push(index);
    }
    const queue = [...starts];
    const previous = new Map(starts.map((index) => [index, null]));
    while (queue.length) {
        const current = queue.shift();
        const row = cellRow(current, HX_SIZE);
        const col = cellCol(current, HX_SIZE);
        const reached = seat === 0 ? row === HX_SIZE - 1 : col === HX_SIZE - 1;
        if (reached) {
            const path = [];
            let cursor = current;
            while (cursor !== null) {
                path.push(cursor);
                cursor = previous.get(cursor) ?? null;
            }
            return path.reverse();
        }
        for (const [dr, dc] of HX_NEIGHBOURS) {
            const nextRow = row + dr;
            const nextCol = col + dc;
            if (!inside(nextRow, nextCol, HX_SIZE, HX_SIZE))
                continue;
            const next = cellIndex(nextRow, nextCol, HX_SIZE);
            if (board[next] !== stone || previous.has(next))
                continue;
            previous.set(next, current);
            queue.push(next);
        }
    }
    return null;
}
/* ------------------------------------------------------------------ */
/* Backgammon                                                          */
/* ------------------------------------------------------------------ */
const BG_POINTS = 24;
export const BACKGAMMON_BAR = 24;
export const BACKGAMMON_OFF = 25;
const backgammon = {
    cols: 12,
    rows: 2,
    create() {
        const board = emptyBoard(BG_POINTS, 1);
        setBackgammonPoint(board, 23, 0, 2);
        setBackgammonPoint(board, 12, 0, 5);
        setBackgammonPoint(board, 7, 0, 3);
        setBackgammonPoint(board, 5, 0, 5);
        setBackgammonPoint(board, 0, 1, 2);
        setBackgammonPoint(board, 11, 1, 5);
        setBackgammonPoint(board, 16, 1, 3);
        setBackgammonPoint(board, 18, 1, 5);
        return {
            kind: 'backgammon',
            cols: 12,
            rows: 2,
            board,
            turn: 0,
            outcome: null,
            moveCount: 0,
            meta: {
                dice: rollBackgammonDice().join(','),
                bar0: 0,
                bar1: 0,
                off0: 0,
                off1: 0,
            },
        };
    },
    legalMoves: backgammonLegalMoves,
    apply(state, move) {
        const next = clone(state);
        applyBackgammonBoardMove(next, move);
        next.lastMove = move;
        next.moveCount += 1;
        if (backgammonMeta(next, 'off', state.turn) >= 15) {
            next.outcome = { winner: state.turn, reason: 'All 15 checkers borne off' };
            return next;
        }
        const remaining = backgammonDice(next);
        if (remaining.length && backgammonLegalMoves(next).length)
            return next;
        beginBackgammonTurn(next, other(state.turn));
        return next;
    },
    notation(state, move) {
        const from = move.from === BACKGAMMON_BAR ? 'bar' : backgammonPointLabel(move.from, state.turn);
        const to = move.to === BACKGAMMON_OFF ? 'off' : backgammonPointLabel(move.to, state.turn);
        const hit = move.to < BG_POINTS && backgammonPoint(state.board, move.to)?.seat === other(state.turn)
            ? '*'
            : '';
        return `${from}/${to}${hit} · ${move.die}`;
    },
};
function rollBackgammonDice() {
    const first = Math.floor(Math.random() * 6) + 1;
    const second = Math.floor(Math.random() * 6) + 1;
    return first === second ? [first, first, first, first] : [first, second];
}
function backgammonDice(state) {
    return String(metaOf(state).dice ?? '')
        .split(',')
        .map(Number)
        .filter((die) => die >= 1 && die <= 6);
}
function backgammonMeta(state, key, seat) {
    return Number(metaOf(state)[`${key}${seat}`] ?? 0);
}
function backgammonPoint(board, point) {
    const code = board[point];
    if (!code)
        return null;
    return { seat: code[0] === 'A' ? 0 : 1, count: Number(code.slice(1)) };
}
function setBackgammonPoint(board, point, seat, count) {
    board[point] = count > 0 ? `${seat === 0 ? 'A' : 'B'}${count}` : '';
}
function backgammonPointLabel(point, seat) {
    return String(seat === 0 ? point + 1 : BG_POINTS - point);
}
function backgammonCanLand(board, point, seat) {
    const target = backgammonPoint(board, point);
    return !target || target.seat === seat || target.count === 1;
}
function backgammonCanBearOff(state, seat) {
    if (backgammonMeta(state, 'bar', seat) > 0)
        return false;
    return state.board.every((code, point) => {
        const stack = backgammonPoint(state.board, point);
        if (!stack || stack.seat !== seat)
            return true;
        return seat === 0 ? point <= 5 : point >= 18;
    });
}
function backgammonCanOvershoot(state, from, seat) {
    if (seat === 0) {
        return state.board.every((_, point) => {
            const stack = backgammonPoint(state.board, point);
            return point <= from || !stack || stack.seat !== seat;
        });
    }
    return state.board.every((_, point) => {
        const stack = backgammonPoint(state.board, point);
        return point >= from || !stack || stack.seat !== seat;
    });
}
function backgammonMovesForDie(state, die) {
    const seat = state.turn;
    const moves = [];
    if (backgammonMeta(state, 'bar', seat) > 0) {
        const to = seat === 0 ? BG_POINTS - die : die - 1;
        return backgammonCanLand(state.board, to, seat) ? [{ from: BACKGAMMON_BAR, to, die }] : [];
    }
    for (let from = 0; from < BG_POINTS; from += 1) {
        const stack = backgammonPoint(state.board, from);
        if (!stack || stack.seat !== seat)
            continue;
        const to = from + (seat === 0 ? -die : die);
        if (to >= 0 && to < BG_POINTS) {
            if (backgammonCanLand(state.board, to, seat))
                moves.push({ from, to, die });
            continue;
        }
        if (!backgammonCanBearOff(state, seat))
            continue;
        const distance = seat === 0 ? from + 1 : BG_POINTS - from;
        if (die === distance || (die > distance && backgammonCanOvershoot(state, from, seat))) {
            moves.push({ from, to: BACKGAMMON_OFF, die });
        }
    }
    return moves;
}
function backgammonRawMoves(state, dice = backgammonDice(state)) {
    const uniqueDice = [...new Set(dice)];
    return uniqueDice.flatMap((die) => backgammonMovesForDie(state, die));
}
function backgammonMoveKey(move) {
    return `${move.from}-${move.to}-${move.die}`;
}
function backgammonSequenceDepth(state, dice) {
    const moves = backgammonRawMoves(state, dice);
    if (!moves.length)
        return 0;
    let best = 0;
    for (const move of moves) {
        const next = clone(state);
        applyBackgammonBoardMove(next, move);
        best = Math.max(best, 1 + backgammonSequenceDepth(next, backgammonDice(next)));
    }
    return best;
}
function backgammonLegalMoves(state) {
    const dice = backgammonDice(state);
    const candidates = backgammonRawMoves(state, dice);
    if (!candidates.length)
        return [];
    const scored = candidates.map((move) => {
        const next = clone(state);
        applyBackgammonBoardMove(next, move);
        return { move, depth: 1 + backgammonSequenceDepth(next, backgammonDice(next)) };
    });
    const maxDepth = Math.max(...scored.map(({ depth }) => depth));
    let best = scored.filter(({ depth }) => depth === maxDepth).map(({ move }) => move);
    // When only one of two distinct dice can be played, Backgammon requires the higher die.
    if (maxDepth === 1 && dice.length === 2 && dice[0] !== dice[1]) {
        const high = Math.max(...best.map((move) => move.die));
        best = best.filter((move) => move.die === high);
    }
    return [...new Map(best.map((move) => [backgammonMoveKey(move), move])).values()];
}
function applyBackgammonBoardMove(state, move) {
    const seat = state.turn;
    const die = move.die;
    if (move.from === BACKGAMMON_BAR) {
        state.meta[`bar${seat}`] = backgammonMeta(state, 'bar', seat) - 1;
    }
    else {
        const source = backgammonPoint(state.board, move.from);
        setBackgammonPoint(state.board, move.from, seat, (source?.count ?? 1) - 1);
    }
    if (move.to === BACKGAMMON_OFF) {
        state.meta[`off${seat}`] = backgammonMeta(state, 'off', seat) + 1;
    }
    else {
        const target = backgammonPoint(state.board, move.to);
        if (target && target.seat !== seat) {
            state.meta[`bar${target.seat}`] = backgammonMeta(state, 'bar', target.seat) + 1;
            setBackgammonPoint(state.board, move.to, seat, 1);
        }
        else {
            setBackgammonPoint(state.board, move.to, seat, (target?.count ?? 0) + 1);
        }
    }
    const dice = backgammonDice(state);
    const used = dice.indexOf(die);
    if (used >= 0)
        dice.splice(used, 1);
    state.meta.dice = dice.join(',');
}
function beginBackgammonTurn(state, firstSeat) {
    let seat = firstSeat;
    // A player whose bar entries are all blocked loses that roll automatically.
    for (let attempts = 0; attempts < 8; attempts += 1) {
        state.turn = seat;
        state.meta.dice = rollBackgammonDice().join(',');
        if (backgammonRawMoves(state).length) {
            state.meta.passed = attempts > 0 ? 1 : 0;
            return;
        }
        seat = other(seat);
    }
}
/* ------------------------------------------------------------------ */
/* Reversi                                                             */
/* ------------------------------------------------------------------ */
const RV_SIZE = 8;
const RV_DIRS = [
    [-1, -1],
    [-1, 0],
    [-1, 1],
    [0, -1],
    [0, 1],
    [1, -1],
    [1, 0],
    [1, 1],
];
const reversiDisc = (seat) => (seat === 0 ? 'B' : 'W');
const reversi = {
    cols: RV_SIZE,
    rows: RV_SIZE,
    create() {
        const board = emptyBoard(RV_SIZE, RV_SIZE);
        board[cellIndex(3, 3, RV_SIZE)] = 'W';
        board[cellIndex(3, 4, RV_SIZE)] = 'B';
        board[cellIndex(4, 3, RV_SIZE)] = 'B';
        board[cellIndex(4, 4, RV_SIZE)] = 'W';
        return {
            kind: 'reversi',
            cols: RV_SIZE,
            rows: RV_SIZE,
            board,
            turn: 0,
            outcome: null,
            moveCount: 0,
            meta: { passed: 0 },
        };
    },
    legalMoves: (state) => reversiMovesFor(state.board, state.turn),
    apply(state, move) {
        const next = clone(state);
        const disc = reversiDisc(state.turn);
        next.board[move.to] = disc;
        for (const flipped of reversiFlips(state.board, move.to, state.turn)) {
            next.board[flipped] = disc;
        }
        next.lastMove = move;
        next.moveCount += 1;
        next.meta.passed = 0;
        // A player with no legal reply passes; if neither side can move, count up.
        const opponent = other(state.turn);
        if (reversiMovesFor(next.board, opponent).length > 0) {
            next.turn = opponent;
        }
        else if (reversiMovesFor(next.board, state.turn).length > 0) {
            next.turn = state.turn;
            next.meta.passed = 1;
        }
        else {
            next.outcome = reversiScoreOutcome(next.board);
        }
        return next;
    },
    notation(state, move) {
        const flips = reversiFlips(state.board, move.to, state.turn).length;
        return `${reversiDisc(state.turn)}${squareName(move.to, RV_SIZE, RV_SIZE)}×${flips}`;
    },
};
function reversiFlips(board, to, seat) {
    if (board[to])
        return [];
    const mine = reversiDisc(seat);
    const theirs = reversiDisc(other(seat));
    const row = cellRow(to, RV_SIZE);
    const col = cellCol(to, RV_SIZE);
    const flips = [];
    for (const [dr, dc] of RV_DIRS) {
        const run = [];
        let r = row + dr;
        let c = col + dc;
        while (inside(r, c, RV_SIZE, RV_SIZE) && board[cellIndex(r, c, RV_SIZE)] === theirs) {
            run.push(cellIndex(r, c, RV_SIZE));
            r += dr;
            c += dc;
        }
        if (run.length &&
            inside(r, c, RV_SIZE, RV_SIZE) &&
            board[cellIndex(r, c, RV_SIZE)] === mine) {
            flips.push(...run);
        }
    }
    return flips;
}
function reversiMovesFor(board, seat) {
    const moves = [];
    for (let i = 0; i < board.length; i += 1) {
        if (!board[i] && reversiFlips(board, i, seat).length > 0)
            moves.push({ to: i });
    }
    return moves;
}
function reversiScoreOutcome(board) {
    const black = board.filter((c) => c === 'B').length;
    const white = board.filter((c) => c === 'W').length;
    if (black === white)
        return { winner: null, reason: `Level at ${black}–${white}` };
    return black > white
        ? { winner: 0, reason: `Black wins ${black}–${white}` }
        : { winner: 1, reason: `White wins ${white}–${black}` };
}
/* ------------------------------------------------------------------ */
/* Checkers (English draughts)                                         */
/* ------------------------------------------------------------------ */
const CK_SIZE = 8;
/** Plies without a capture or a crowning before the game is called a draw. */
const CK_IDLE_LIMIT = 80;
const checkersSeat = (code) => {
    if (code === 'r' || code === 'R')
        return 0;
    if (code === 'b' || code === 'B')
        return 1;
    return null;
};
const isKing = (code) => code === 'R' || code === 'B';
const checkers = {
    cols: CK_SIZE,
    rows: CK_SIZE,
    create() {
        const board = emptyBoard(CK_SIZE, CK_SIZE);
        for (let row = 0; row < CK_SIZE; row += 1) {
            for (let col = 0; col < CK_SIZE; col += 1) {
                if ((row + col) % 2 === 0)
                    continue; // pieces only live on dark squares
                if (row < 3)
                    board[cellIndex(row, col, CK_SIZE)] = 'b';
                if (row > 4)
                    board[cellIndex(row, col, CK_SIZE)] = 'r';
            }
        }
        return {
            kind: 'checkers',
            cols: CK_SIZE,
            rows: CK_SIZE,
            board,
            turn: 0,
            outcome: null,
            moveCount: 0,
            meta: { chain: -1, idle: 0 },
        };
    },
    legalMoves: (state) => checkersMoves(state.board, state.turn, Number(metaOf(state).chain ?? -1)),
    apply(state, move) {
        const next = clone(state);
        const from = move.from;
        const piece = state.board[from];
        next.board[from] = '';
        const jumped = checkersJumpedCell(from, move.to);
        if (jumped >= 0)
            next.board[jumped] = '';
        const landingRow = cellRow(move.to, CK_SIZE);
        const crowned = !isKing(piece) &&
            ((state.turn === 0 && landingRow === 0) ||
                (state.turn === 1 && landingRow === CK_SIZE - 1));
        next.board[move.to] = crowned ? piece.toUpperCase() : piece;
        next.lastMove = move;
        next.moveCount += 1;
        // Multi-jumps keep the turn, but crowning always ends it.
        const canContinue = jumped >= 0 &&
            !crowned &&
            checkersMoves(next.board, state.turn, move.to).some((m) => m.from === move.to);
        next.meta.idle = jumped >= 0 || crowned ? 0 : Number(state.meta.idle ?? 0) + 1;
        if (canContinue) {
            next.meta.chain = move.to;
        }
        else {
            next.meta.chain = -1;
            next.turn = other(state.turn);
            const replies = checkersMoves(next.board, next.turn, -1);
            if (replies.length === 0) {
                next.outcome = {
                    winner: state.turn,
                    reason: checkersPieces(next.board, next.turn) === 0 ? 'No pieces left' : 'No moves left',
                };
            }
            else if (Number(next.meta.idle) >= CK_IDLE_LIMIT) {
                next.outcome = { winner: null, reason: '40 moves without a capture' };
            }
        }
        return next;
    },
    notation(state, move) {
        const from = squareName(move.from, CK_SIZE, CK_SIZE);
        const to = squareName(move.to, CK_SIZE, CK_SIZE);
        return `${from}${checkersJumpedCell(move.from, move.to) >= 0 ? '×' : '–'}${to}`;
    },
};
/** The cell hopped over, or -1 when the move was a simple slide. */
function checkersJumpedCell(from, to) {
    const dr = cellRow(to, CK_SIZE) - cellRow(from, CK_SIZE);
    const dc = cellCol(to, CK_SIZE) - cellCol(from, CK_SIZE);
    if (Math.abs(dr) !== 2 || Math.abs(dc) !== 2)
        return -1;
    return cellIndex(cellRow(from, CK_SIZE) + dr / 2, cellCol(from, CK_SIZE) + dc / 2, CK_SIZE);
}
function checkersPieces(board, seat) {
    return board.filter((code) => code && checkersSeat(code) === seat).length;
}
/**
 * Captures are compulsory: when any jump exists, only jumps are returned. A
 * `chain` cell restricts the list further to the piece mid-multi-jump.
 */
function checkersMoves(board, seat, chain) {
    const jumps = [];
    const slides = [];
    for (let from = 0; from < board.length; from += 1) {
        const piece = board[from];
        if (!piece || checkersSeat(piece) !== seat)
            continue;
        if (chain >= 0 && from !== chain)
            continue;
        const row = cellRow(from, CK_SIZE);
        const col = cellCol(from, CK_SIZE);
        const forward = seat === 0 ? -1 : 1;
        const dirs = isKing(piece) ? [-1, 1] : [forward];
        for (const dr of dirs) {
            for (const dc of [-1, 1]) {
                const sr = row + dr;
                const sc = col + dc;
                if (!inside(sr, sc, CK_SIZE, CK_SIZE))
                    continue;
                const step = cellIndex(sr, sc, CK_SIZE);
                if (!board[step]) {
                    if (chain < 0)
                        slides.push({ from, to: step });
                    continue;
                }
                if (checkersSeat(board[step]) === seat)
                    continue;
                const jr = row + dr * 2;
                const jc = col + dc * 2;
                if (!inside(jr, jc, CK_SIZE, CK_SIZE))
                    continue;
                const landing = cellIndex(jr, jc, CK_SIZE);
                if (!board[landing])
                    jumps.push({ from, to: landing });
            }
        }
    }
    return jumps.length > 0 ? jumps : slides;
}
/* ------------------------------------------------------------------ */
/* Chess                                                               */
/* ------------------------------------------------------------------ */
const CH_SIZE = 8;
const KNIGHT_DELTAS = [
    [-2, -1],
    [-2, 1],
    [-1, -2],
    [-1, 2],
    [1, -2],
    [1, 2],
    [2, -1],
    [2, 1],
];
const KING_DELTAS = [
    [-1, -1],
    [-1, 0],
    [-1, 1],
    [0, -1],
    [0, 1],
    [1, -1],
    [1, 0],
    [1, 1],
];
const BISHOP_DIRS = [
    [-1, -1],
    [-1, 1],
    [1, -1],
    [1, 1],
];
const ROOK_DIRS = [
    [-1, 0],
    [1, 0],
    [0, -1],
    [0, 1],
];
const PROMOTION_PIECES = ['Q', 'R', 'B', 'N'];
const chessSeat = (code) => {
    if (!code)
        return null;
    return code === code.toUpperCase() ? 0 : 1;
};
const chessPiece = (code) => code.toUpperCase();
/** Seat 0 is White at the bottom of the array, so it moves toward row 0. */
const pawnDir = (seat) => (seat === 0 ? -1 : 1);
const chess = {
    cols: CH_SIZE,
    rows: CH_SIZE,
    create() {
        const board = emptyBoard(CH_SIZE, CH_SIZE);
        const back = 'rnbqkbnr';
        for (let col = 0; col < CH_SIZE; col += 1) {
            board[cellIndex(0, col, CH_SIZE)] = back[col];
            board[cellIndex(1, col, CH_SIZE)] = 'p';
            board[cellIndex(6, col, CH_SIZE)] = 'P';
            board[cellIndex(7, col, CH_SIZE)] = back[col].toUpperCase();
        }
        return {
            kind: 'chess',
            cols: CH_SIZE,
            rows: CH_SIZE,
            board,
            turn: 0,
            outcome: null,
            moveCount: 0,
            meta: { castling: 'KQkq', ep: -1, halfmove: 0 },
        };
    },
    legalMoves: (state) => chessLegalMoves(state.board, state.turn, metaOf(state)),
    apply(state, move) {
        const next = clone(state);
        const piece = state.board[move.from];
        const kind = chessPiece(piece);
        const captured = state.board[move.to];
        const ep = Number(metaOf(state).ep ?? -1);
        const isEnPassant = kind === 'P' && move.to === ep && !captured;
        applyChessMove(next.board, move, piece);
        next.meta.castling = nextCastlingRights(String(metaOf(state).castling ?? ''), move, piece, captured);
        next.meta.ep = enPassantSquare(move, piece);
        next.meta.halfmove =
            kind === 'P' || captured || isEnPassant ? 0 : Number(metaOf(state).halfmove ?? 0) + 1;
        next.lastMove = move;
        next.moveCount += 1;
        next.turn = other(state.turn);
        const replies = chessLegalMoves(next.board, next.turn, next.meta);
        if (replies.length === 0) {
            next.outcome = inCheck(next.board, next.turn)
                ? { winner: state.turn, reason: 'Checkmate' }
                : { winner: null, reason: 'Stalemate' };
        }
        else if (Number(next.meta.halfmove) >= 100) {
            next.outcome = { winner: null, reason: 'Fifty-move rule' };
        }
        else if (insufficientMaterial(next.board)) {
            next.outcome = { winner: null, reason: 'Insufficient material' };
        }
        return next;
    },
    notation: (state, move) => chessNotation(state, move),
};
/** Moves the piece, including the rook in a castle and an en passant capture. */
function applyChessMove(board, move, piece) {
    const seat = chessSeat(piece);
    const kind = chessPiece(piece);
    const from = move.from;
    const fromCol = cellCol(from, CH_SIZE);
    const toCol = cellCol(move.to, CH_SIZE);
    const row = cellRow(from, CH_SIZE);
    if (kind === 'P' && toCol !== fromCol && !board[move.to]) {
        // En passant: the captured pawn sits beside the destination, not on it.
        board[cellIndex(row, toCol, CH_SIZE)] = '';
    }
    board[from] = '';
    if (kind === 'K' && Math.abs(toCol - fromCol) === 2) {
        const kingside = toCol > fromCol;
        const rookFrom = cellIndex(row, kingside ? 7 : 0, CH_SIZE);
        const rookTo = cellIndex(row, kingside ? 5 : 3, CH_SIZE);
        board[rookTo] = board[rookFrom];
        board[rookFrom] = '';
    }
    if (kind === 'P' &&
        (cellRow(move.to, CH_SIZE) === 0 || cellRow(move.to, CH_SIZE) === CH_SIZE - 1)) {
        const promoted = (move.promotion ?? 'Q').toUpperCase();
        board[move.to] = seat === 0 ? promoted : promoted.toLowerCase();
        return;
    }
    board[move.to] = piece;
}
/** The square a pawn just skipped over, which the opponent may capture into. */
function enPassantSquare(move, piece) {
    const fromRow = cellRow(move.from, CH_SIZE);
    const toRow = cellRow(move.to, CH_SIZE);
    if (chessPiece(piece) !== 'P' || Math.abs(toRow - fromRow) !== 2)
        return -1;
    return cellIndex((fromRow + toRow) / 2, cellCol(move.to, CH_SIZE), CH_SIZE);
}
function nextCastlingRights(rights, move, piece, captured) {
    let next = rights;
    const drop = (chars) => {
        next = next
            .split('')
            .filter((c) => !chars.includes(c))
            .join('');
    };
    const kind = chessPiece(piece);
    const seat = chessSeat(piece);
    if (kind === 'K')
        drop(seat === 0 ? 'KQ' : 'kq');
    if (kind === 'R') {
        if (move.from === cellIndex(7, 0, CH_SIZE))
            drop('Q');
        if (move.from === cellIndex(7, 7, CH_SIZE))
            drop('K');
        if (move.from === cellIndex(0, 0, CH_SIZE))
            drop('q');
        if (move.from === cellIndex(0, 7, CH_SIZE))
            drop('k');
    }
    // A rook captured on its home square can never castle either.
    if (captured && chessPiece(captured) === 'R') {
        if (move.to === cellIndex(7, 0, CH_SIZE))
            drop('Q');
        if (move.to === cellIndex(7, 7, CH_SIZE))
            drop('K');
        if (move.to === cellIndex(0, 0, CH_SIZE))
            drop('q');
        if (move.to === cellIndex(0, 7, CH_SIZE))
            drop('k');
    }
    return next || '-';
}
function chessPseudoMoves(board, seat, meta) {
    const moves = [];
    const ep = Number(meta.ep ?? -1);
    const rights = String(meta.castling ?? '');
    const push = (from, to) => moves.push({ from, to });
    const slide = (from, row, col, dirs) => {
        for (const [dr, dc] of dirs) {
            let r = row + dr;
            let c = col + dc;
            while (inside(r, c, CH_SIZE, CH_SIZE)) {
                const target = cellIndex(r, c, CH_SIZE);
                const occupant = board[target];
                if (!occupant)
                    push(from, target);
                else {
                    if (chessSeat(occupant) !== seat)
                        push(from, target);
                    break;
                }
                r += dr;
                c += dc;
            }
        }
    };
    for (let from = 0; from < board.length; from += 1) {
        const piece = board[from];
        if (!piece || chessSeat(piece) !== seat)
            continue;
        const row = cellRow(from, CH_SIZE);
        const col = cellCol(from, CH_SIZE);
        const kind = chessPiece(piece);
        if (kind === 'P') {
            const dir = pawnDir(seat);
            const startRow = seat === 0 ? 6 : 1;
            const oneRow = row + dir;
            if (inside(oneRow, col, CH_SIZE, CH_SIZE) && !board[cellIndex(oneRow, col, CH_SIZE)]) {
                pushPawn(moves, from, cellIndex(oneRow, col, CH_SIZE), seat);
                const twoRow = row + dir * 2;
                if (row === startRow && !board[cellIndex(twoRow, col, CH_SIZE)]) {
                    push(from, cellIndex(twoRow, col, CH_SIZE));
                }
            }
            for (const dc of [-1, 1]) {
                const c = col + dc;
                if (!inside(oneRow, c, CH_SIZE, CH_SIZE))
                    continue;
                const target = cellIndex(oneRow, c, CH_SIZE);
                const occupant = board[target];
                if ((occupant && chessSeat(occupant) !== seat) || (!occupant && target === ep)) {
                    pushPawn(moves, from, target, seat);
                }
            }
            continue;
        }
        if (kind === 'N') {
            for (const [dr, dc] of KNIGHT_DELTAS) {
                const r = row + dr;
                const c = col + dc;
                if (!inside(r, c, CH_SIZE, CH_SIZE))
                    continue;
                const target = cellIndex(r, c, CH_SIZE);
                if (!board[target] || chessSeat(board[target]) !== seat)
                    push(from, target);
            }
            continue;
        }
        if (kind === 'B')
            slide(from, row, col, BISHOP_DIRS);
        if (kind === 'R')
            slide(from, row, col, ROOK_DIRS);
        if (kind === 'Q')
            slide(from, row, col, [...BISHOP_DIRS, ...ROOK_DIRS]);
        if (kind === 'K') {
            for (const [dr, dc] of KING_DELTAS) {
                const r = row + dr;
                const c = col + dc;
                if (!inside(r, c, CH_SIZE, CH_SIZE))
                    continue;
                const target = cellIndex(r, c, CH_SIZE);
                if (!board[target] || chessSeat(board[target]) !== seat)
                    push(from, target);
            }
            const homeRow = seat === 0 ? 7 : 0;
            if (row === homeRow && col === 4 && !squareAttacked(board, from, other(seat))) {
                const kingRight = seat === 0 ? 'K' : 'k';
                const queenRight = seat === 0 ? 'Q' : 'q';
                const empty = (...cols) => cols.every((c) => !board[cellIndex(homeRow, c, CH_SIZE)]);
                const safe = (c) => !squareAttacked(board, cellIndex(homeRow, c, CH_SIZE), other(seat));
                // Rights should imply the rook is home, but never castle with thin air.
                const rook = (c) => {
                    const code = board[cellIndex(homeRow, c, CH_SIZE)];
                    return code && chessPiece(code) === 'R' && chessSeat(code) === seat;
                };
                if (rights.includes(kingRight) && rook(7) && empty(5, 6) && safe(5)) {
                    push(from, cellIndex(homeRow, 6, CH_SIZE));
                }
                if (rights.includes(queenRight) && rook(0) && empty(1, 2, 3) && safe(3)) {
                    push(from, cellIndex(homeRow, 2, CH_SIZE));
                }
            }
        }
    }
    return moves;
}
function pushPawn(moves, from, to, seat) {
    const lastRow = seat === 0 ? 0 : CH_SIZE - 1;
    if (cellRow(to, CH_SIZE) === lastRow) {
        for (const promotion of PROMOTION_PIECES)
            moves.push({ from, to, promotion });
    }
    else {
        moves.push({ from, to });
    }
}
function squareAttacked(board, index, bySeat) {
    const row = cellRow(index, CH_SIZE);
    const col = cellCol(index, CH_SIZE);
    const belongs = (code, kind) => code && chessSeat(code) === bySeat && chessPiece(code) === kind;
    for (const [dr, dc] of KNIGHT_DELTAS) {
        const r = row + dr;
        const c = col + dc;
        if (inside(r, c, CH_SIZE, CH_SIZE) && belongs(board[cellIndex(r, c, CH_SIZE)], 'N'))
            return true;
    }
    for (const [dr, dc] of KING_DELTAS) {
        const r = row + dr;
        const c = col + dc;
        if (inside(r, c, CH_SIZE, CH_SIZE) && belongs(board[cellIndex(r, c, CH_SIZE)], 'K'))
            return true;
    }
    // Pawns capture toward their own direction of travel, so look back along it.
    const backDir = -pawnDir(bySeat);
    for (const dc of [-1, 1]) {
        const r = row + backDir;
        const c = col + dc;
        if (inside(r, c, CH_SIZE, CH_SIZE) && belongs(board[cellIndex(r, c, CH_SIZE)], 'P'))
            return true;
    }
    const rays = [
        [BISHOP_DIRS, ['B', 'Q']],
        [ROOK_DIRS, ['R', 'Q']],
    ];
    for (const [dirs, kinds] of rays) {
        for (const [dr, dc] of dirs) {
            let r = row + dr;
            let c = col + dc;
            while (inside(r, c, CH_SIZE, CH_SIZE)) {
                const occupant = board[cellIndex(r, c, CH_SIZE)];
                if (occupant) {
                    if (chessSeat(occupant) === bySeat && kinds.includes(chessPiece(occupant)))
                        return true;
                    break;
                }
                r += dr;
                c += dc;
            }
        }
    }
    return false;
}
function kingSquare(board, seat) {
    const king = seat === 0 ? 'K' : 'k';
    return board.indexOf(king);
}
function inCheck(board, seat) {
    const king = kingSquare(board, seat);
    return king >= 0 && squareAttacked(board, king, other(seat));
}
function chessLegalMoves(board, seat, meta) {
    return chessPseudoMoves(board, seat, meta).filter((move) => {
        const trial = board.slice();
        applyChessMove(trial, move, board[move.from]);
        return !inCheck(trial, seat);
    });
}
function insufficientMaterial(board) {
    const minor = [];
    for (let i = 0; i < board.length; i += 1) {
        const kind = board[i] ? chessPiece(board[i]) : '';
        if (!kind || kind === 'K')
            continue;
        if (kind === 'P' || kind === 'R' || kind === 'Q')
            return false;
        minor.push(i);
    }
    if (minor.length <= 1)
        return true;
    if (minor.length > 2)
        return false;
    // Two bishops only draw when they can never meet — same square colour.
    const bishops = minor.filter((i) => chessPiece(board[i]) === 'B');
    if (bishops.length !== 2)
        return false;
    const colorOf = (i) => (cellRow(i, CH_SIZE) + cellCol(i, CH_SIZE)) % 2;
    return (chessSeat(board[bishops[0]]) !== chessSeat(board[bishops[1]]) &&
        colorOf(bishops[0]) === colorOf(bishops[1]));
}
function chessNotation(state, move) {
    const piece = state.board[move.from];
    const kind = chessPiece(piece);
    const seat = state.turn;
    const fromCol = cellCol(move.from, CH_SIZE);
    const toCol = cellCol(move.to, CH_SIZE);
    const captured = state.board[move.to];
    const isEnPassant = kind === 'P' && !captured && toCol !== fromCol;
    let text;
    if (kind === 'K' && Math.abs(toCol - fromCol) === 2) {
        text = toCol > fromCol ? 'O-O' : 'O-O-O';
    }
    else {
        const target = squareName(move.to, CH_SIZE, CH_SIZE);
        if (kind === 'P') {
            const capture = captured || isEnPassant ? `${FILES[fromCol]}×` : '';
            const promotion = move.promotion ? `=${move.promotion.toUpperCase()}` : '';
            text = `${capture}${target}${promotion}`;
        }
        else {
            text = `${kind}${disambiguate(state, move, kind)}${captured ? '×' : ''}${target}`;
        }
    }
    const after = state.board.slice();
    applyChessMove(after, move, piece);
    const opponent = other(seat);
    if (inCheck(after, opponent)) {
        const replies = chessLegalMoves(after, opponent, {
            castling: nextCastlingRights(String(metaOf(state).castling ?? ''), move, piece, captured),
            ep: enPassantSquare(move, piece),
        });
        text += replies.length === 0 ? '#' : '+';
    }
    return text;
}
/** `Nbd2` / `R1a3` — only as much origin as it takes to be unambiguous. */
function disambiguate(state, move, kind) {
    const rivals = chessLegalMoves(state.board, state.turn, metaOf(state)).filter((m) => m.to === move.to &&
        m.from !== move.from &&
        chessPiece(state.board[m.from] ?? '') === kind);
    if (rivals.length === 0)
        return '';
    const fromCol = cellCol(move.from, CH_SIZE);
    const fromRow = cellRow(move.from, CH_SIZE);
    if (!rivals.some((m) => cellCol(m.from, CH_SIZE) === fromCol))
        return FILES[fromCol];
    if (!rivals.some((m) => cellRow(m.from, CH_SIZE) === fromRow))
        return String(CH_SIZE - fromRow);
    return squareName(move.from, CH_SIZE, CH_SIZE);
}
/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */
const ENGINES = {
    tic_tac_toe: ticTacToe,
    connect_four: connectFour,
    gomoku,
    hex,
    backgammon,
    reversi,
    checkers,
    chess,
};
export function createGameState(kind) {
    return ENGINES[kind].create();
}
export function legalMoves(state) {
    if (state.outcome)
        return [];
    return ENGINES[state.kind].legalMoves(state);
}
/** Legal destinations for one piece — what the board highlights on select. */
export function legalMovesFrom(state, from) {
    return legalMoves(state).filter((move) => (move.from ?? -1) === from);
}
export function moveNotation(state, move) {
    return ENGINES[state.kind].notation(state, move);
}
function matches(candidate, wanted) {
    if (candidate.to !== wanted.to)
        return false;
    if ((candidate.from ?? -1) !== (wanted.from ?? -1))
        return false;
    if ((candidate.die ?? -1) !== (wanted.die ?? -1))
        return false;
    // A promotion move without a stated piece is taken to be a queen.
    if (candidate.promotion) {
        return candidate.promotion.toUpperCase() === (wanted.promotion ?? 'Q').toUpperCase();
    }
    return true;
}
/**
 * The single entry point for making a move. The server calls it to decide
 * whether a move is allowed; the client calls it to preview one locally.
 */
export function playMove(state, move, seat) {
    if (state.outcome)
        return { ok: false, error: 'This game has already finished' };
    if (seat !== undefined && seat !== state.turn) {
        return { ok: false, error: 'It is not your turn' };
    }
    const legal = ENGINES[state.kind].legalMoves(state).find((m) => matches(m, move));
    if (!legal)
        return { ok: false, error: 'That is not a legal move' };
    return {
        ok: true,
        move: legal,
        notation: ENGINES[state.kind].notation(state, legal),
        state: ENGINES[state.kind].apply(state, legal),
    };
}
/** True when the side to move is in check — chess only, for the board glow. */
export function isInCheck(state) {
    return state.kind === 'chess' && inCheck(state.board, state.turn);
}
